import { Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { PrestationEmptyStateComponent } from '../prestation-empty-state/prestation-empty-state.component';
import { TripService } from 'src/app/trip-calculator/services/trip.service';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TooltipIconComponent } from 'src/app/shared/tooltip-icon/tooltip-icon.component';
import { InputNumberModule } from 'primeng/inputnumber';
import { Waste, WasteType } from 'src/app/trip-calculator/models/waste';
import { PopoverModule } from 'primeng/popover';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  selector: 'app-waste-section',
  imports: [
    ButtonModule,
    TranslocoPipe,
    PrestationEmptyStateComponent,
    ReactiveFormsModule,
    CheckboxModule,
    SelectModule,
    TooltipIconComponent,
    InputNumberModule,
    PopoverModule,
    ConfirmDialogModule,
  ],
  templateUrl: './waste-section.component.html',
})
export class WasteSectionComponent {
  tripService = inject(TripService);
  fb = inject(FormBuilder);
  translocoService = inject(TranslocoService);
  confirmationService = inject(ConfirmationService);
  destroyRef = inject(DestroyRef);
  wasteForm?: FormGroup;
  loading = false;

  constructor() {
    effect(() => {
      const trip = this.tripService.trip();
      if (trip && trip.wasteType && !this.wasteForm) {
        this.createWasteForm();
        this.updateWasteForm(
          trip.wasteType as WasteType,
          trip.kgOfPlastic,
          trip.kgOfNonPlastic,
        );
      }
    });
  }

  removeWaste() {
    this.tripService.deleteWaste()?.subscribe(() => {
      this.wasteForm = undefined;
    });
  }
  submit() {
    this.loading = true;
    const type = this.wasteForm?.get('type')!.value as WasteType;
    const isSpecific = type === WasteType.Specific;
    const waste: Waste = {
      type,
      // Only `specific` consumes these quantities server-side. Sending them for the
      // other types would leave stale figures behind in database.
      kgOfPlastic: isSpecific
        ? this.wasteForm?.get('kgOfPlastic')?.value
        : undefined,
      kgOfNonPlastic: isSpecific
        ? this.wasteForm?.get('kgOfNonPlastic')?.value
        : undefined,
    };
    this.tripService.updateWaste(waste)?.subscribe({
      next: () => {
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
  updateWasteForm(
    type: WasteType,
    kgOfPlastic?: number,
    kgOfNonPlastic?: number,
  ) {
    this.wasteForm?.patchValue(
      {
        type,
        kgOfPlastic,
        kgOfNonPlastic,
      },
      { emitEvent: false },
    );
    this.syncQuantityValidators(type);
  }
  createWasteForm = () => {
    // No default quantities here: the API resolves the standard averages itself for
    // every type but `specific`.
    this.wasteForm = this.fb.group({
      type: ['', Validators.required],
      kgOfPlastic: [null, [Validators.min(0)]],
      kgOfNonPlastic: [null, [Validators.min(0)]],
    });
    this.wasteForm
      .get('type')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((type: WasteType) => this.syncQuantityValidators(type));
  };

  /** Quantities are only editable — and only mandatory — in `specific` mode. */
  private syncQuantityValidators(type: WasteType) {
    const validators =
      type === WasteType.Specific
        ? [Validators.required, Validators.min(0)]
        : [Validators.min(0)];

    for (const name of ['kgOfPlastic', 'kgOfNonPlastic']) {
      const control = this.wasteForm?.get(name);
      control?.setValidators(validators);
      control?.updateValueAndValidity({ emitEvent: false });
    }
  }
  confirmDelete() {
    const message = this.translocoService.translate('confirmation.deleteWaste');
    const header = this.translocoService.translate(
      'confirmation.deleteConfirmation',
    );
    this.confirmationService.confirm({
      message: message,
      header: header,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('yes'),
      rejectLabel: this.translocoService.translate('no'),
      accept: () => this.removeWaste(),
    });
  }
}
